import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const SHIFT_END = "Mesai Bitimi";
// Reasons that require a manager approval selection.
// SHIFT_END is intentionally excluded — it's a normal end-of-day departure.
const REASONS = [
  "Doktor / Sağlık",
  "Aile / Acil",
  "Resmi İşlem",
  "Kişisel",
  "Ders / Eğitim",
  "Diğer",
];

/**
 * Modal shown after a checkout on public terminal flows.
 * Lets the employee optionally pick a reason + approving manager.
 * Fully skippable via "Atla" — reason is not required for normal shift-end exits.
 * If the employee picks "Mesai Bitimi" (shift end), the approver section is hidden
 * and the record is saved without an approver.
 */
export default function EarlyExitForm({ attendanceId, employeeName, earlyExit = false, onDone }) {
  const [managers, setManagers] = useState([]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [approverId, setApproverId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/users/managers").then(({ data }) => setManagers(data)).catch(() => {});
  }, []);

  const isShiftEnd = reason === SHIFT_END;

  const submit = async () => {
    if (!reason) { toast.error("Lütfen sebep seçin"); return; }
    setBusy(true);
    try {
      const approver = isShiftEnd ? null : managers.find((m) => m.id === approverId);
      await api.patch(`/attendance/${attendanceId}/early-exit`, {
        reason,
        note,
        approver_id: isShiftEnd ? null : (approverId || null),
        approver_name: approver?.name || null,
      });
      toast.success(isShiftEnd ? "Mesai bitimi kaydedildi" : "Çıkış bilgileri kaydedildi");
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const title = earlyExit ? "Erken Çıkış" : "Çıkış Sebebi";
  const subtitle = earlyExit
    ? "Mesai bitmeden çıkıyorsunuz. Lütfen sebep seçin veya atlayın."
    : "Çıkış sebebinizi seçebilirsiniz. Normal mesai sonu ise atlayabilirsiniz.";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-xl flex items-center justify-center p-4"
        data-testid="early-exit-form"
      >
        <motion.div
          initial={{ scale: 0.9, y: 12 }} animate={{ scale: 1, y: 0 }}
          className="w-full max-w-md rounded-2xl border border-white/10 text-white p-6"
          style={{ background: "rgba(20,20,26,0.95)" }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-white/50">{title}</div>
              <h2 className="font-display text-xl font-bold tracking-tight">{employeeName}</h2>
              <p className="text-white/50 text-xs mt-1">{subtitle}</p>
            </div>
            <button onClick={onDone} className="text-white/60 hover:text-white" data-testid="early-exit-skip">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Prominent quick-select for the most common case */}
            {!earlyExit && (
              <button
                type="button"
                onClick={() => { setReason(SHIFT_END); setApproverId(""); }}
                data-testid="reason-shift-end"
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-colors duration-100 ${
                  isShiftEnd
                    ? "bg-emerald-500/25 border-emerald-500 text-emerald-100"
                    : "bg-emerald-500/5 border-emerald-500/40 hover:bg-emerald-500/15 text-emerald-100/90"
                }`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isShiftEnd ? "bg-emerald-500 text-white" : "bg-emerald-500/20 text-emerald-300"}`}>
                  <Check className="w-5 h-5" strokeWidth={3} />
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-base leading-tight">Mesai Bitimi</div>
                  <div className="text-xs text-emerald-100/70 mt-0.5">
                    Normal mesai bitimi · yönetici onayı gerekmez
                  </div>
                </div>
              </button>
            )}

            <div>
              <Label className="text-white/80 text-xs uppercase tracking-wider mb-2 block">
                {isShiftEnd ? "Farklı Bir Sebep" : "Sebep"}
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {REASONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setReason(r)}
                    data-testid={`reason-${r.split(" ")[0].toLowerCase()}`}
                    className={`px-3 py-2.5 rounded-lg border text-sm text-left transition-colors duration-100 ${
                      reason === r
                        ? "bg-blue-600 border-blue-500 text-white"
                        : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Manager approval only when reason is NOT the shift-end quick action */}
            {!isShiftEnd && (
              <div>
                <Label className="text-white/80 text-xs uppercase tracking-wider mb-2 block">Onaylayan Yönetici</Label>
                <div className="max-h-40 overflow-y-auto space-y-1 rounded-lg border border-white/10 p-1 bg-white/5">
                  {managers.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setApproverId(m.id)}
                      data-testid={`approver-${m.id}`}
                      className={`w-full px-3 py-2 rounded-md text-sm text-left flex items-center justify-between ${
                        approverId === m.id ? "bg-blue-600 text-white" : "hover:bg-white/10 text-white/80"
                      }`}
                    >
                      <span>{m.name}</span>
                      <span className="text-xs uppercase tracking-wider text-white/50">{m.role}</span>
                    </button>
                  ))}
                  {managers.length === 0 && <div className="text-xs text-white/50 p-2">Yönetici bulunamadı.</div>}
                </div>
              </div>
            )}

            <div>
              <Label className="text-white/80 text-xs uppercase tracking-wider mb-2 block">Not (opsiyonel)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Kısa açıklama..."
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
                data-testid="early-exit-note"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={onDone} className="flex-1" data-testid="early-exit-skip-btn">
                Atla
              </Button>
              <Button
                onClick={submit}
                disabled={busy || !reason}
                className={`flex-1 ${isShiftEnd ? "bg-emerald-600 hover:bg-emerald-500" : "bg-blue-600 hover:bg-blue-500"}`}
                data-testid="early-exit-save"
              >
                {busy ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
